import * as React from "react"
import { Button, VStack, Text, Center } from "@chakra-ui/react"
import { useRecoilValue } from "recoil"
import { brandingSelector } from "../../../recoil/atoms/room"
import { viewDependingLayoutAtom } from "../../../recoil/atoms/view"
import DeniedPermissionWarning from "../../settings/DeniedPermissionWarning"

export interface LocalButtonProps {
  customstyles?: React.CSSProperties
  isHidden?: boolean
  isDisabled?: boolean
  showSelecedStyling?: boolean
  enabled?: boolean
  setter?: (status: boolean) => void
  handler?: () => void
  id: string
  icons: React.ReactElement[]
  names?: any
  isVerticalButton?: boolean
  isMobile?: boolean
  styling?: React.CSSProperties
  bg?: string
  isFirst?: boolean
  isLast?: boolean
  isRoundButton?: boolean
  isWithoutTitle?: boolean
  users?: number
  isActive?: boolean
  haveModal?: boolean
  isMainMobileScreen?: boolean
  numberColumn?: number
  showWarningIcon?: boolean
}

const LocalButton = React.memo((props: LocalButtonProps) => {
  const {
    setter,
    handler,
    isVerticalButton,
    enabled,
    icons,
    isMobile,
    isHidden,
    isDisabled,
    showSelecedStyling,
    id,
    names,
    isRoundButton,
    isWithoutTitle,
    users,
    customstyles = {},
    isActive = false,
    haveModal,
    isMainMobileScreen,
    showWarningIcon,
    isFirst = false,
    isLast = false
  } = props

  const { isTablet } = useRecoilValue(viewDependingLayoutAtom)

  const handleOnClick = React.useCallback(
    e => {
      e.stopPropagation()
      e.preventDefault()
      if (setter) {
        setter(!enabled)
      }
      if (handler) {
        handler()
      }
    },
    [enabled, handler, setter]
  )

  const sizeRoundButton = React.useMemo(
    () => (users < 3 ? "35px" : "25px"),
    [users]
  )
  const buttonWidth = React.useMemo(() => {
    if (haveModal) {
      return "100%"
    }
    if (isRoundButton) {
      return sizeRoundButton
    }
    return !isVerticalButton
      ? "calc((66.66vw - 100px) / 7)"
      : "calc(66.66vw / 4)"
  }, [haveModal, isRoundButton, isVerticalButton, sizeRoundButton])

  const buttonBorderRadius = React.useMemo(() => {
    if (isRoundButton) {
      return "50%"
    }
    return !isVerticalButton ? "md" : "0px"
  }, [isRoundButton, isVerticalButton])

  const icon = React.useMemo(() => {
    if (icons.length > 1 && enabled) {
      return icons[1]
    }
    return icons[0]
  }, [enabled, icons])
  const roomStyles = useRecoilValue(brandingSelector)

  const Content = React.useCallback(() => {
    return (
      <VStack flexDirection={isVerticalButton && !isMobile ? "row" : "column"}>
        <Center
          minW="1rem"
          fontSize={
            isMainMobileScreen
              ? "1.1rem"
              : isRoundButton && (users < 3 ? "1.5rem" : "1rem")
          }
        >
          {icon}
        </Center>
        {!isWithoutTitle && (
          <Center
            minW="1rem"
            marginTop={isVerticalButton && "0px !important"}
            marginLeft={isVerticalButton && !isMobile && "0.5rem !important"}
          >
            <Text
              fontSize={
                enabled
                  ? names[1].length > 9
                    ? "0.65rem"
                    : "0.75em"
                  : names[0].length > 9
                  ? "0.65rem"
                  : "0.75em"
              }
            >
              {enabled ? names[1] : names[0]}
            </Text>
          </Center>
        )}
      </VStack>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    icon,
    isMainMobileScreen,
    isMobile,
    isRoundButton,
    isVerticalButton,
    isWithoutTitle,
    names
  ])

  const activeStyles = isActive
    ? {
        bg: roomStyles?.accessButtonSelectedColor ?? "#F7FAFC",
        textColor: roomStyles?.accessButtonSelectedTextColor ?? "#000000"
      }
    : {
        bg: roomStyles?.accessButtonBackgroundColor
          ? roomStyles.accessButtonBackgroundColor
          : "#FFFFFF",
        textColor: roomStyles?.accessButtonTextColor
          ? roomStyles.accessButtonTextColor
          : "#718096"
      }

  const selectedStyles = {
    bg: roomStyles?.accessButtonSelectedColor
      ? roomStyles.accessButtonSelectedColor
      : "#F7FAFC",
    textColor: roomStyles?.accessButtonSelectedTextColor
      ? roomStyles.accessButtonSelectedTextColor
      : "#000000"
  }

  const emptyArray = React.useMemo(() => {
    return {}
  }, [])
  const styleHover = React.useMemo(() => {
    return {
      backgroundColor: roomStyles?.accessButtonMouseoverColor
        ? roomStyles.accessButtonMouseoverColor
        : "#F7FAFC",
      textColor: roomStyles?.accessButtonTextMouseoverColor
        ? roomStyles.accessButtonTextMouseoverColor
        : "#000000"
    }
  }, [
    roomStyles.accessButtonMouseoverColor,
    roomStyles.accessButtonTextMouseoverColor
  ])

  if (isHidden) return null

  return (
    <Button
      style={customstyles}
      key={id}
      py={!isWithoutTitle && "1.5rem"}
      marginInlineStart={isVerticalButton && "0px !important"}
      marginInlineEnd={isVerticalButton && "0px !important"}
      w={isMainMobileScreen ? "42px" : buttonWidth}
      minW={!isWithoutTitle && "63px"}
      height={isMainMobileScreen ? "42px" : isRoundButton && sizeRoundButton}
      maxW={!isVerticalButton ? "300px" : "300px"}
      px="0px"
      bg={
        isMainMobileScreen
          ? roomStyles.joinButtonBackgroundColor
          : showSelecedStyling
          ? selectedStyles.bg
          : activeStyles.bg
      }
      borderRight={
        props.isLast
          ? `1px solid ${
              roomStyles?.accessButtonBorderColor
                ? roomStyles.accessButtonBorderColor
                : "#e2e8f0"
            }`
          : "0px"
      }
      borderColor={
        roomStyles?.accessButtonBorderColor
          ? roomStyles.accessButtonBorderColor
          : "#e2e8f0"
      }
      color={
        roomStyles?.accessButtonBorderColor
          ? roomStyles.accessButtonBorderColor
          : "#e2e8f0"
      }
      textColor={
        isMainMobileScreen
          ? roomStyles.joinPageButtonTextColor
          : showSelecedStyling
          ? selectedStyles.textColor
          : activeStyles.textColor
      }
      borderRadius={buttonBorderRadius}
      borderTopLeftRadius={isRoundButton ? null : isFirst ? "4px" : 0}
      borderBottomLeftRadius={isRoundButton ? null : isFirst ? "4px" : 0}
      borderTopRightRadius={isRoundButton ? null : isLast ? "4px" : 0}
      borderBottomRightRadius={isRoundButton ? null : isLast ? "4px" : 0}
      _hover={(!isMainMobileScreen || isTablet) && styleHover}
      _focus={!isMainMobileScreen && emptyArray}
      _active={!isMainMobileScreen && emptyArray}
      onClick={handleOnClick}
      onTouchStart={handleOnClick}
      disabled={isDisabled}
      marginRight={
        isMainMobileScreen
          ? "12px !important"
          : isMobile && !isMainMobileScreen
          ? users < 3
            ? "5px !important"
            : "3px !important"
          : "unset"
      }
      _last={
        (isMainMobileScreen || isMobile) && { marginRight: "0px !important" }
      }
    >
      <DeniedPermissionWarning
        isDenied={showWarningIcon}
        top={"-18px"}
        right={"-12px"}
      >
        <Content />
      </DeniedPermissionWarning>
    </Button>
  )
})

export default LocalButton
